// Default "post-reveal" journey blocks for the homepage (and any microsite that
// hasn't configured its own). A campaign can override this via campaign.journeys
// (edited in the Admin console). Each path -> { nudge, modules[] }, and every
// module carries a `visible` flag + a `type` that maps to a renderer.

export const DEFAULT_GIRI_WHATSAPP = "917208920560";
export const DEFAULT_BOOK_URL = "";

// Pre-filled "Sponsor a Bhagavatam" prices (whole ₹) — every microsite starts
// with these and can edit them in the Admin console.
export const DEFAULT_SPONSOR = {
  enabled: true,
  prices: { en: 8100, hi: 6400, mr: 5900 },
};

// Quick-tap donation amounts shown under the amount field.
export const SPONSOR_QUICK_AMOUNTS = [100, 500, 1000];

// Short tags used to auto-segment leads in GiriChat (prepended to the WhatsApp
// message as e.g. "Interested - Bhagavatam · juhu").
export const PATH_TAGS = {
  bhagavatam: "Bhagavatam",
  mahaprasadam: "Prasadam",
  chanting: "Chanting",
  darshan: "Darshan",
};

const BHAGAVATAM_VIDEOS = [
  "https://www.facebook.com/reel/1325305765487899",
  "https://www.youtube.com/shorts/kbohDLxCJHI",
  "https://www.youtube.com/shorts/d8srw_HrrZE",
  "https://www.youtube.com/shorts/_9hA4-7l2SQ",
  "https://www.youtube.com/shorts/0uiUaHOa1GY",
  "https://www.youtube.com/shorts/mrL26DoVna0",
  "https://www.youtube.com/shorts/Z9OPjHIVXEM",
  "https://www.youtube.com/shorts/Kg-tSZO5AVE",
  "https://www.youtube.com/shorts/BAfqrR62Jyg",
];

const CANTO_TITLES = [
  ["Canto 1 · Creation", ["Vyasa's compassion", "Pariksit is cursed", "Sukadeva arrives"]],
  ["Canto 2 · The Cosmic Manifestation", ["The path of liberation", "The universal form"]],
  ["Canto 3 · The Status Quo", ["Vidura's pilgrimage", "Kapila's teachings"]],
  ["Canto 4 · The Creation of the Fourth Order", ["Prithu Maharaj", "Lord Shiva & the Prachetas"]],
  ["Canto 5 · The Creative Impetus", ["Rishabhadeva", "Bharata Maharaj", "Cosmic structure"]],
  ["Canto 6 · Prescribed Duties for Mankind", ["Ajamila delivered", "The Hare Krishna maha-mantra"]],
  ["Canto 7 · The Science of God", ["Prahlada Maharaj", "Lord Nrisimhadeva"]],
  ["Canto 8 · Withdrawal of the Cosmic Creations", ["Gajendra's prayers", "Churning the ocean", "Vamanadeva"]],
  ["Canto 9 · Liberation", ["The dynasties of kings", "Lord Ramachandra"]],
  ["Canto 10 · The Summum Bonum", ["Krishna's birth", "Vrindavana pastimes", "The rasa dance"]],
  ["Canto 11 · General History", ["Uddhava-gita", "The final teachings"]],
  ["Canto 12 · The Age of Deterioration", ["Signs of Kali-yuga", "Glories of the Bhagavatam"]],
];

const defaultCantos = () =>
  CANTO_TITLES.map(([title, lines], i) => ({
    id: `canto-${i + 1}`,
    cover: "",
    title,
    lines,
  }));

const conversion = (headline, body, wa_label, wa_message) => ({
  id: "conversion",
  type: "conversion",
  visible: true,
  headline,
  body,
  name_field: true,
  wa_label,
  wa_message,
  book_enabled: true,
  book_label: "Book Your Copy Directly",
});

export const DEFAULT_JOURNEYS = {
  bhagavatam: {
    nudge: "Scroll to begin your Bhagavatam journey",
    modules: [
      {
        id: "vani",
        type: "video_carousel",
        visible: true,
        title: "Vani — Hear the Glories",
        subtitle: "Senior devotees speak on the glories of Srimad Bhagavatam.",
        videos: BHAGAVATAM_VIDEOS,
      },
      {
        id: "darshan",
        type: "canto_carousel",
        visible: true,
        title: "Darshan of the Twelve Cantos",
        subtitle: "Eighteen thousand verses across twelve sacred volumes.",
        cantos: defaultCantos(),
      },
      {
        id: "impact",
        type: "gallery",
        visible: true,
        title: "Received Across the World",
        subtitle: "Seekers everywhere are bringing the Bhagavatam home.",
        images: ["", "", "", "", ""],
      },
      {
        id: "arrival",
        type: "photo_story",
        visible: true,
        title: "From Our Hands to Your Altar",
        subtitle: "We handle delivery, installation, kirtan and prasadam — effortlessly.",
        steps: [
          { image: "", caption: "Your set is lovingly packed and dispatched." },
          { image: "", caption: "Delivered to your doorstep with care." },
          { image: "", caption: "Installed on your home altar." },
          { image: "", caption: "A joyful kirtan blesses your home." },
          { image: "", caption: "Prasadam is shared with family & friends." },
        ],
      },
      conversion(
        "Bring Srimad Bhagavatam Home",
        "Begin a lifetime with Krishna's words. Share your name and we'll guide you personally.",
        "I am Interested — Chat on WhatsApp",
        "Hare Krishna! I'm interested in bringing Srimad Bhagavatam home. {name}"
      ),
    ],
  },
  mahaprasadam: {
    nudge: "Scroll to receive His mercy",
    modules: [
      {
        id: "offering",
        type: "video_carousel",
        visible: true,
        title: "The Offering",
        subtitle: "Watch how mercy is cooked, offered and shared.",
        videos: [],
      },
      {
        id: "feast",
        type: "gallery",
        visible: true,
        title: "The Community Feast",
        subtitle: "Sanctified food that nourishes body and soul.",
        images: ["", "", "", ""],
      },
      conversion(
        "Receive Mahaprasadam",
        "Let His mercy reach your home and community.",
        "Request Mahaprasadam — Chat on WhatsApp",
        "Hare Krishna! I would like to request Mahaprasadam. {name}"
      ),
    ],
  },
  chanting: {
    nudge: "Scroll to Experience the Holy Name",
    modules: [
      {
        id: "audio",
        type: "audio_sample",
        visible: true,
        title: "Hear the Holy Name",
        subtitle: "A gentle sample of the maha-mantra to chant along.",
        src: "",
      },
      {
        id: "mala",
        type: "mala_counter",
        visible: true,
        title: "Chant a Round",
        subtitle: "Tap the bead 108 times to complete one round.",
      },
      conversion(
        "Begin Your Practice",
        "Join a chanting circle and be supported on the path.",
        "Join Chanting Circle — Chat on WhatsApp",
        "Hare Krishna! I'd like to join a chanting circle. {name}"
      ),
    ],
  },
  darshan: {
    nudge: "Scroll for His divine presence",
    modules: [
      {
        id: "altar",
        type: "gallery",
        visible: true,
        title: "The Virtual Altar",
        subtitle: "Invite His beautiful form into your home.",
        images: ["", "", "", ""],
      },
      conversion(
        "Bring Home Darshan",
        "Receive a blessed wallpaper or framed darshan for your home.",
        "Receive Darshan — Chat on WhatsApp",
        "Hare Krishna! I'd love to receive a Darshan wallpaper/frame. {name}"
      ),
    ],
  },
};

// ---- helpers ----

export function parseVideo(url) {
  const u = (url || "").trim();
  if (!u) return null;
  let id = "";
  // youtube shorts / watch / youtu.be
  const shorts = u.match(/youtube\.com\/shorts\/([\w-]+)/i);
  const watch = u.match(/[?&]v=([\w-]+)/i);
  const short = u.match(/youtu\.be\/([\w-]+)/i);
  if (shorts) id = shorts[1];
  else if (watch) id = watch[1];
  else if (short) id = short[1];
  if (id) {
    return {
      url: u,
      platform: "youtube",
      id,
      thumb: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    };
  }
  if (/facebook\.com/i.test(u)) return { url: u, platform: "facebook", id: "", thumb: "" };
  if (/instagram\.com/i.test(u)) return { url: u, platform: "instagram", id: "", thumb: "" };
  return { url: u, platform: "link", id: "", thumb: "" };
}

// A media module is "empty" until real content is added — used to auto-hide
// image galleries / photo stories on the public site until an admin uploads.
export function moduleHasContent(m) {
  if (m.type === "video_carousel") return (m.videos || []).some((v) => v && v.trim());
  if (m.type === "gallery") return (m.images || []).some((x) => x && x.trim());
  if (m.type === "photo_story") return (m.steps || []).some((s) => s.image && s.image.trim());
  return true;
}

export function waLink(number, message, name, tag) {
  let digits = String(number || DEFAULT_GIRI_WHATSAPP).replace(/\D/g, "");
  if (digits.length === 10) digits = "91" + digits;
  const nameBit = name && name.trim() ? `My name is ${name.trim()}.` : "";
  let text = (message || "Hare Krishna!").replace("{name}", nameBit).trim();
  if (tag) text = `${tag}\n\n${text}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}
